import React, { useState, useMemo } from 'react';
import { List, Badge, Select, Input, Space, Typography, Tag, Tooltip } from 'antd';
import { SearchOutlined, ClockCircleOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import './chat.css';

const { Option } = Select;
const { Text } = Typography;

/**
 * Утилита для обрезания строки
 */
function truncateText(text, maxLength = 20) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Форматирование времени
 */
function formatTime(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Teraz';
  if (diffMins < 60) return `${diffMins}m temu`;
  if (diffHours < 24) return `${diffHours}h temu`;
  if (diffDays < 7) return `${diffDays}d temu`;

  return date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
}

const ChatList = ({ chats, onSelectChat, selectedChatId }) => {
  // Выбор статуса (все/открытые/закрытые) - по умолчанию открытые
  const [statusFilter, setStatusFilter] = useState('open');
  // Фильтр по прочитанным/непрочитанным
  const [readFilter, setReadFilter] = useState('all');
  // Поиск по имени/фамилии
  const [searchTerm, setSearchTerm] = useState('');

  /**
   * 1) Фильтруем список чатов
   */
  const filteredChats = useMemo(() => {
    return chats.filter((chat) => {
      // 1. Фильтрация по статусу
      if (statusFilter !== 'all' && chat.status !== statusFilter) {
        return false;
      }

      // 2. Фильтрация по прочитанности
      const isUnread = (chat.unreadCount || 0) > 0;
      if (readFilter === 'unread' && !isUnread) {
        return false;
      }
      if (readFilter === 'read' && isUnread) {
        return false;
      }

      // 3. Поиск по имени/фамилии
      const creatorName = (chat.creatorName || '').toLowerCase();
      const chatName = (chat.chatName || '').toLowerCase();
      const search = searchTerm.toLowerCase();

      if (!creatorName.includes(search) && !chatName.includes(search)) {
        return false;
      }

      return true;
    });
  }, [chats, statusFilter, readFilter, searchTerm]);

  /**
   * 2) Сортируем отфильтрованные чаты
   */
  const sortedChats = useMemo(() => {
    return [...filteredChats].sort((a, b) => {
      // Сортировка по времени последнего сообщения (новые сверху)
      const aTime = a.lastMessage?.timestamp || a.timestamp || 0;
      const bTime = b.lastMessage?.timestamp || b.timestamp || 0;
      return new Date(bTime) - new Date(aTime);
    });
  }, [filteredChats]);

  /**
   * Рендер элемента списка чата
   */
  const renderChatItem = (chat) => {
    const isUnread = (chat.unreadCount || 0) > 0;
    const isSelected = selectedChatId === chat.id;
    const isOpen = chat.status === 'open';

    const lastMessageText = chat.lastMessage?.text || 'Brak wiadomości';
    const lastMessageTime = chat.lastMessage?.timestamp || chat.timestamp;

    return (
      <div
        key={chat.id}
        className={`chat-list-item ${isSelected ? 'selected' : ''} ${isUnread ? 'unread' : ''}`}
        onClick={() => onSelectChat(chat.id)}
      >
        {/* Левый индикатор */}
        <div className={`chat-indicator ${isUnread ? 'unread' : ''}`} />

        {/* Основной контент */}
        <div className="chat-content">
          {/* Заголовок */}
          <div className="chat-header">
            <div className="chat-title-section">
              <UserOutlined className="chat-icon" />
              <Text strong className="chat-name">
                {truncateText(chat.chatName || chat.creatorName || 'Nieznany', 25)}
              </Text>
              {!isOpen && (
                <Tag icon={<CloseCircleOutlined />} color="default" className="status-tag">
                  Zamknięty
                </Tag>
              )}
            </div>

            <div className="chat-time">
              <Text type="secondary" className="time-text">
                {formatTime(lastMessageTime)}
              </Text>
            </div>
          </div>

          {/* Последнее сообщение */}
          <div className="chat-preview">
            <Text type="secondary" className="preview-text">
              {truncateText(lastMessageText, 50)}
            </Text>
          </div>

          {/* Футер с метаданными */}
          <div className="chat-footer">
            <div className="chat-meta">
              <Text type="secondary" className="meta-text">
                {chat.creatorName || 'Nieznany użytkownik'}
              </Text>
            </div>

            {isUnread && (
              <Badge
                count={chat.unreadCount}
                className="unread-badge"
                style={{ backgroundColor: '#ff4d4f' }}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="chat-list-container">
      {/* Заголовок */}
      <div className="chat-list-header">
        <Text strong style={{ fontSize: '18px' }}>Czaty</Text>
        <Badge count={sortedChats.length} showZero style={{ backgroundColor: '#52c41a' }} />
      </div>

      {/* Фильтры */}
      <div className="chat-filters">
        <Input
          placeholder="Szukaj czatu..."
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
          className="search-input"
        />

        <div style={{ marginTop: '8px', width: '100%', display: 'flex', gap: '8px' }}>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ flex: 1 }}
            size="middle"
          >
            <Option value="all">
              <Space>
                <CheckCircleOutlined />
                Wszystkie
              </Space>
            </Option>
            <Option value="open">
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                Otwarte
              </Space>
            </Option>
            <Option value="closed">
              <Space>
                <CloseCircleOutlined style={{ color: '#8c8c8c' }} />
                Zamknięte
              </Space>
            </Option>
          </Select>

          <Select
            value={readFilter}
            onChange={setReadFilter}
            style={{ flex: 1 }}
            size="middle"
          >
            <Option value="all">Wszystkie</Option>
            <Option value="unread">
              <Space>
                <Badge status="error" />
                Nieprzeczytane
              </Space>
            </Option>
            <Option value="read">
              <Space>
                <Badge status="success" />
                Przeczytane
              </Space>
            </Option>
          </Select>
        </div>
      </div>

      {/* Список чатов */}
      <div className="chat-list-scroll">
        {sortedChats.length > 0 ? (
          sortedChats.map(renderChatItem)
        ) : (
          <div className="empty-state">
            <ClockCircleOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
            <Text type="secondary" style={{ marginTop: '16px' }}>
              Brak czatów
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatList;
